import React, { useCallback, useMemo, useState } from 'react';
import { ReorderableList } from '../reorderable-list';
import {
  Ellipsis,
  GetColor,
  Icon,
  ItemIcon,
  ItemType,
  ItemTypePill,
  ShimmerBlock,
  Tooltip,
  TooltipPosition,
} from 'venn-ui-kit';
import {
  AnalysisSubject,
  getBlockAllowsSecondaryAboveSubjectLimit,
  getRequestSubjectForSecondaryAnalysisSubject,
  getRequestSubjectFromAnalysisSubject,
  Numbers,
  useHasFF,
  withSuspense,
} from 'venn-utils';
import styled, { css } from 'styled-components';
import type { StudioRequestSubject, Subject } from 'venn-state';
import {
  allocatorAnalysisSubject,
  analysisSubjectQuery,
  analysisSubjects,
  blockInfoGraphicType,
  isReportState,
  rightPanelFees,
  selectedBlockIdState,
} from 'venn-state';
import { constSelector, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import AllocationPanelLauncherAction from '../allocation-panel/studio/AllocationPanelLauncherAction';
import { FeesRelativePopover, SubjectIcon } from './FeesRelativePopover';
import SecondarySubjectLine from './SecondarySubjectLine';
import { compact, isNil } from 'lodash';

interface SubjectItemData {
  key: string;
  value: Subject;
  primary: StudioRequestSubject;
  secondary?: StudioRequestSubject;
  primaryIndex: number;
}

interface SubjectItemProps extends SubjectItemData {
  hideAllocatorLauncher?: boolean;
  reorderable: boolean;
  maxSubjects: number;
  hideItemTypePill?: boolean;
}

const SubjectItem = withSuspense(
  <ShimmerBlock height={24} margin={0} />,
  ({
    hideAllocatorLauncher,
    reorderable,
    maxSubjects,
    value: subject,
    primary,
    secondary,
    primaryIndex,
    hideItemTypePill,
  }: SubjectItemProps) => {
    const analysisSubject = useRecoilValue(analysisSubjectQuery(subject));
    const [fees, setFees] = useRecoilState(rightPanelFees);
    const fee = fees[analysisSubject.id];
    const setFee = (value: number) => {
      setFees((current) => ({ ...current, [analysisSubject.id]: value }));
    };
    const [showFee, setShowFee] = useState(false);

    const selectedBlockId = useRecoilValue(selectedBlockIdState);
    const infoGraphicType = useRecoilValue(
      selectedBlockId ? blockInfoGraphicType(selectedBlockId) : constSelector(undefined),
    );
    const allowSecondaryAboveLimit = getBlockAllowsSecondaryAboveSubjectLimit(infoGraphicType);

    const disabled = primaryIndex >= maxSubjects;
    const secondaryExcluded = primaryIndex + 1 >= maxSubjects + (allowSecondaryAboveLimit ? 1 : 0);

    const setAllocatorAnalysisSubject = useSetRecoilState(allocatorAnalysisSubject(subject));
    const onDeleteSecondaryItem = useCallback(() => {
      if (isNil(analysisSubject.portfolio) || isNil(analysisSubject.secondaryPortfolio)) {
        return;
      }
      setAllocatorAnalysisSubject(
        new AnalysisSubject(analysisSubject.portfolio, 'portfolio', {
          ...analysisSubject.getOptionsCopy(),
          secondaryPortfolio: undefined,
          secondaryLabel: undefined,
        }),
      );
    }, [analysisSubject, setAllocatorAnalysisSubject]);

    const isReport = useRecoilValue(isReportState);
    const isSelectedStrategyOrInvestment =
      !isNil(primary.portfolio) && !isNil(primary.strategy) && primary.portfolio.id !== primary.strategy.id;
    const hasInvestmentFees = useHasFF('investment_advisory_fees_ff');
    // If a fee currently exists for an investment, always allow them to edit it
    const feeEnabled = reorderable && isReport && (hasInvestmentFees || analysisSubject.type === 'portfolio' || fee);

    return (
      <SubjectContainer data-testid="qa-subject">
        <SubjectName disabled={disabled} reorderable={reorderable}>
          {reorderable && isReport && (
            <Tooltip
              content={`Click to ${!fee ? 'add' : 'update'} management fee.`}
              position={TooltipPosition.Right}
              isHidden={!feeEnabled}
              data-testid="qa-fee-icon"
            >
              <StyledIcon
                hidden={!feeEnabled}
                active={!!fee}
                type="dollar"
                onClick={feeEnabled ? () => setShowFee(!showFee) : undefined}
              />
            </Tooltip>
          )}
          {!hideItemTypePill && (
            <ItemTypePill
              item={
                primary.subjectType === 'PORTFOLIO' && !analysisSubject.isInvestmentInPortfolio
                  ? isSelectedStrategyOrInvestment
                    ? ItemType.Strategy
                    : ItemType.Portfolio
                  : ItemType.Investment
              }
            />
          )}
          <MaybeNestedIcons isNested={isSelectedStrategyOrInvestment}>
            <SubjectIcon subject={analysisSubject} />
            {isSelectedStrategyOrInvestment && (
              <>
                <Icon type="corner" />
                <ItemIcon item={primary.strategy!.fund ? ItemType.Investment : ItemType.Portfolio} />
              </>
            )}
          </MaybeNestedIcons>
          <Ellipsis singleLine>{primary.name}</Ellipsis>
          <FloatRight>
            {!hideAllocatorLauncher && <AllocationPanelLauncherAction subject={analysisSubject} />}
            {disabled && (
              <Tooltip
                content="This object has been excluded from this block"
                position={TooltipPosition.Left}
                usePortal
              >
                <BanIcon type="ban" />
              </Tooltip>
            )}
          </FloatRight>
        </SubjectName>
        {!!fee && <FeeSpan>{`${Numbers.safeFormatNumber(fee * 100)}% Advisory Fee`}</FeeSpan>}
        {showFee && (
          <FeesRelativePopover
            analysisSubject={analysisSubject}
            fee={fee}
            setFee={setFee}
            onClose={() => setShowFee(false)}
          />
        )}
        {!isNil(secondary) && (
          <SecondarySubjectLine subject={secondary} onDelete={onDeleteSecondaryItem} excluded={secondaryExcluded} />
        )}
      </SubjectContainer>
    );
  },
);

export type SelectedSubjectsProps = {
  subjects: Subject[];
  /**
   * The callback used when subjects are reordered.
   * Not used when reordeable is false.
   */
  setSubjects: (subjects: Subject[]) => void;
  reorderable?: boolean;
} & Pick<SubjectItemProps, 'hideAllocatorLauncher' | 'hideItemTypePill' | 'maxSubjects'>;

const SelectedSubjects = ({ subjects, setSubjects, reorderable, ...subjectItemProps }: SelectedSubjectsProps) => {
  const allAnalysisSubjects = useRecoilValue(analysisSubjects(subjects));
  const items: SubjectItemData[] = useMemo(() => {
    let totalSubjectCountSoFar = 0;
    return allAnalysisSubjects.flatMap((analysisSubject, index) => {
      const subject = subjects[index];
      const primary = getRequestSubjectFromAnalysisSubject(analysisSubject);
      const secondary = getRequestSubjectForSecondaryAnalysisSubject(analysisSubject);
      const result: SubjectItemData = {
        key: `${subject.portfolioId ?? subject.fundId}`,
        value: subject,
        primary,
        secondary,
        primaryIndex: totalSubjectCountSoFar,
      };
      totalSubjectCountSoFar += compact([primary, secondary]).length;
      return result;
    });
  }, [allAnalysisSubjects, subjects]);

  const onReorderSubjects = (newItems: SubjectItemData[]) => {
    setSubjects(newItems.map((item) => item.value));
  };

  return (
    <>
      {reorderable ? (
        <ReorderableList<SubjectItemData>
          counterMargin={10}
          reorderableItems={items}
          onReorderItems={onReorderSubjects}
          itemRenderer={(item) => <>{item.value && <SubjectItem reorderable {...subjectItemProps} {...item} />}</>}
        />
      ) : (
        <>
          {items.map((item) => (
            <SubjectItem reorderable={false} {...subjectItemProps} {...item} />
          ))}
        </>
      )}
    </>
  );
};

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
export default (props: SelectedSubjectsProps) => (
  <React.Suspense
    fallback={
      <div>
        {props.subjects.map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <ShimmerBlock key={index} height={24} margin={4} />
        ))}
      </div>
    }
  >
    <SelectedSubjects {...props} />
  </React.Suspense>
);

const BanIcon = styled(Icon)`
  margin: 0 4px;
`;

const FloatRight = styled.div`
  margin-left: auto;
  padding-right: 5px;
  display: flex;
`;

const StyledIcon = styled(Icon)<{ active: boolean; hidden?: boolean }>`
  ${({ hidden }) =>
    hidden
      ? css`
          opacity: 0;
        `
      : css`
          &:hover {
            cursor: pointer;
            color: ${GetColor.Primary.Main};
          }
        `}
  font-size: 14px;
  margin: 0 4px;
  line-height: 14px;
  color: ${({ active }) => (active ? GetColor.Primary.Dark : GetColor.Grey)};
`;

const SubjectName = styled.div<{ disabled?: boolean; reorderable: boolean }>`
  display: flex;
  align-items: center;
  column-gap: 3px;
  height: 24px;
  font-size: 1.125rem;

  /* TODO(will, collin): IMV2 P2: disabled grey color isn't relevant to subject groups in some situations; maybe a prop should be provided to adjust this behavior? */
  ${(props) =>
    !props.reorderable &&
    css`
      color: ${GetColor.GreyScale.Grey70};
    `}

  ${(props) =>
    props.disabled &&
    css`
      color: ${GetColor.GreyScale.Grey70};

      .qa-Investment-type-pill,
      .qa-Portfolio-type-pill {
        background-color: ${GetColor.GreyScale.Grey60};
      }

      i {
        color: ${GetColor.GreyScale.Grey60};
      }
    `}
`;

const FeeSpan = styled.span`
  display: flex;
  flex-direction: row;
  height: 16px;
  font-size: 12px;
  align-items: center;
  margin-left: 28px;
  color: ${GetColor.DarkGrey};
  font-style: italic;
`;

const SubjectContainer = styled.div`
  display: flex;
  flex-direction: column;
  /* Allow shrinking and truncation with min-width 0 when parent is also flex e.g. within ReorderableList. */
  min-width: 0;
  flex: 1;
`;

const MaybeNestedIcons = styled.span<{ isNested: boolean }>`
  ${({ isNested }) =>
    isNested &&
    css`
      position: relative;
      min-width: 1.5rem;
      & > i,
      & > span {
        position: absolute;
      }
      & > i:nth-child(1),
      & > span:nth-child(1) {
        top: -10px;
        left: -2px;
        font-size: 7px;
      }
      & > i:nth-child(2) {
        transform: rotate(180deg);
        font-weight: normal;
        top: -6px;
        left: 1px;
        font-size: 9px;
        color: ${GetColor.DarkGrey};
      }
      & > i:nth-child(3) {
        left: 4px;
        top: -4px;
      }
    `}
`;
